import * as React from "react";
import { observer } from "mobx-react";
import axios from "axios";
import call from "../../call";
import downloadBlob from "../../download";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";
import TableSortLabel from "@mui/material/TableSortLabel";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import Title from "./Title";
import Pagination from "@mui/material/Pagination";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";

// The task's name or CID as a link that opens its inline editor, or a prompt to add one.
const renderEditLink = (value, placeholder, onClick) => (
  <Link
    component="button"
    variant="body2"
    onClick={onClick}
    sx={{
      color: value ? "#333" : "#1976d2",
      textDecoration: "none",
      "&:hover": { textDecoration: "underline" },
    }}
  >
    {value || placeholder}
  </Link>
);

const sanitizeFilePart = (v) => (v || "").replaceAll(/[^\w\s-]/g, "").replaceAll(/\s+/g, "_");

const pad = (n) => String(n).padStart(2, "0");

const downloadTask = (task) => {
  call(
    axios({
      method: "get",
      url: `/api/demandbase/task_download/${task.id}`,
      headers: { "content-type": "application/json", accept: "application/csv" },
      responseType: "blob",
    })
  ).then((resp) => {
    const clientName = sanitizeFilePart(task.client?.name);
    const listName = sanitizeFilePart(task.listName);
    const listId = sanitizeFilePart(task.listId) || "na";
    const now = new Date();
    const timestamp =
      `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
      `_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    downloadBlob(
      `demandbase_${clientName}_${listName}_${listId}_task${task.id}_${timestamp}.csv`,
      resp.data,
    );
  });
};

const Tasks = observer(() => {
  const [tasks, setTasks] = React.useState({ data: [], meta: { total: 0, page: 1, limit: 10 } });
  const [page, setPage] = React.useState(1);
  const [selectedTaskIds, setSelectedTaskIds] = React.useState([]);
  const [error, setError] = React.useState("");
  const [editingTaskId, setEditingTaskId] = React.useState(null);
  const [cidDraft, setCidDraft] = React.useState("");
  const [isSavingCid, setIsSavingCid] = React.useState(false);
  const [bulkCidDraft, setBulkCidDraft] = React.useState("");
  const [isBulkSavingCid, setIsBulkSavingCid] = React.useState(false);

  // Sorting state
  const [sortBy, setSortBy] = React.useState("createdAt");
  const [order, setOrder] = React.useState("DESC");

  // Filter state
  const [cidFilter, setCidFilter] = React.useState("");
  const [clientFilter, setClientFilter] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [createdFrom, setCreatedFrom] = React.useState("");
  const [createdTo, setCreatedTo] = React.useState("");
  const [nameFilter, setNameFilter] = React.useState("");

  // Name editing state
  const [editingNameTaskId, setEditingNameTaskId] = React.useState(null);
  const [nameDraft, setNameDraft] = React.useState("");
  const [isSavingName, setIsSavingName] = React.useState(false);

  // Clients for dropdown
  const [clients, setClients] = React.useState([]);

  const update = React.useCallback((newPage, params = {}) => {
      call(
          axios({
              method: "get",
              url: "/api/demandbase/task",
              params: { 
                page: newPage,
                limit: 10,
                ...params,
              },
              headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
              },
          }),
      ).then((resp) => {
          setTasks({
              data: resp.data.data,
              meta: resp.data.meta
          });
      });
  }, [call]);

  // Fetch clients for dropdown on mount
  React.useEffect(() => {
    call(
      axios({
        method: "get",
        url: "/api/demandbase/task/clients",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      })
    ).then((resp) => {
      setClients(resp.data.data || []);
    });
  }, [call]);

  React.useEffect(() => {
    const params = {
      sort_by: sortBy,
      order: order,
      cid: cidFilter,
      client_id: clientFilter || undefined,
      status: statusFilter,
      created_from: createdFrom,
      created_to: createdTo,
      name: nameFilter,
    };

    const timeout = setTimeout(() => {
      update(page, params);
    }, 400);

    return () => clearTimeout(timeout);
  }, [page, sortBy, order, cidFilter, clientFilter, statusFilter, createdFrom, createdTo, nameFilter, update]);

  React.useEffect(() => {
    if (editingTaskId || editingNameTaskId) {
      return;
    }

    const params = {
      sort_by: sortBy,
      order: order,
      cid: cidFilter,
      client_id: clientFilter || undefined,
      status: statusFilter,
      created_from: createdFrom,
      created_to: createdTo,
      name: nameFilter,
    };

    const interval = setInterval(() => update(page, params), 2000);
    return () => {
      clearInterval(interval);
    };
  }, [page, editingTaskId, editingNameTaskId, sortBy, order, cidFilter, clientFilter, statusFilter, createdFrom, createdTo, nameFilter, update]);

  React.useEffect(() => {
    setSelectedTaskIds([]);
  }, [page]);

  const handleRequestSort = (field) => {
    if (sortBy === field) {
      setOrder(order === "ASC" ? "DESC" : "ASC");
    } else {
      setSortBy(field);
      setOrder(field === "createdAt" ? "DESC" : "ASC");
    }
    setPage(1);
  };

  const tableCellStyle = {
    paddingTop: "13px",
    verticalAlign: "top",
  };

  const getStatusChip = (status, statusLabel) => {
    let bgColor = "#e0e0e0";
    let textColor = "#333";
    
    if (status === 2) { // Done
      bgColor = "#e8f5e9";
      textColor = "#2e7d32";
    } else if (status === 1) { // In Progress
      bgColor = "#fff8e1";
      textColor = "#f9a825";
    } else if (status === 3) { // Failed
      bgColor = "#ffebee";
      textColor = "#c62828";
    }
    
    return (
      <Chip
        label={statusLabel || "Unknown"}
        size="small"
        sx={{
          backgroundColor: bgColor,
          color: textColor,
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "24px",
        }}
      />
    );
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    const month = d.toLocaleString("en-US", { month: "short" });
    const day = d.getDate();
    const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
    return `${month} ${day}, ${time}`;
  };

  const visibleTaskIds = tasks.data.map((t) => t.id);
  const isAllSelected = visibleTaskIds.length > 0 && visibleTaskIds.every((id) => selectedTaskIds.includes(id));
  const isSomeSelected = visibleTaskIds.some((id) => selectedTaskIds.includes(id));

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedTaskIds(visibleTaskIds);
      return;
    }
    setSelectedTaskIds([]);
  };

  const handleToggleSelected = (taskId) => {
    setSelectedTaskIds((prev) => {
      if (prev.includes(taskId)) {
        return prev.filter((id) => id !== taskId);
      }
      return [...prev, taskId];
    });
  };

  const startEditingCid = (task) => {
    setEditingTaskId(task.id);
    setCidDraft(task.cid);
  };

  const cancelEditingCid = () => {
    setEditingTaskId(null);
    setCidDraft("");
  };

  const saveCid = (task) => {

    setIsSavingCid(true);

    call(
      axios({
        method: "put",
        url: `/api/demandbase/task/${task.id}`,
        data: {
          cid: cidDraft,
        },
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    )
      .then(() => {
        cancelEditingCid();
        update(page);
      }).catch((e) => {
        setError(
            e.response?.data?.error
                ? e.response.data.error
                : "An error occured on trying to load the client!",
        );
    })
      .finally(() => {
        setIsSavingCid(false);
      });
  };

  const cancelBulkCid = () => {
    setBulkCidDraft("");
    setSelectedTaskIds([]);
  };

  const saveBulkCid = async () => {
    if (!bulkCidDraft || selectedTaskIds.length === 0) {
      return;
    }

    setIsBulkSavingCid(true);

    try {
      for (const id of selectedTaskIds) {
        await call(
          axios({
            method: "put",
            url: `/api/demandbase/task/${id}`,
            data: {
              cid: bulkCidDraft,
            },
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }),
        );
      }

      cancelBulkCid();
      update(page);
    } catch (e) {
      setError(
        e.response?.data?.error
          ? e.response.data.error
          : "An error occured on trying to update selected tasks!",
      );
    } finally {
      setIsBulkSavingCid(false);
    }
  };

  const startEditingName = (task) => {
    setEditingNameTaskId(task.id);
    setNameDraft(task.name || "");
  };

  const cancelEditingName = () => {
    setEditingNameTaskId(null);
    setNameDraft("");
  };

  const saveName = (task) => {
    setIsSavingName(true);

    call(
      axios({
        method: "put",
        url: `/api/demandbase/task/${task.id}`,
        data: {
          name: nameDraft || null,
        },
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    )
      .then(() => {
        cancelEditingName();
        update(page);
      }).catch((e) => {
            setError(
                e.response?.data?.error
                    ? e.response.data.error
                    : "An error occured on trying to update the task name!",
            );
      })
      .finally(() => {
        setIsSavingName(false);
      });
  };

  return (
    <React.Fragment>
      <Title>Tasks</Title>

      {/* Filter bar */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, mt: 2 }}>
        <TextField
          label="Task Name"
          value={nameFilter}
          onChange={(e) => {
            setNameFilter(e.target.value);
            setPage(1);
          }}
          size="small"
          sx={{ minWidth: 140 }}
        />

        <TextField
          label="CID"
          value={cidFilter}
          onChange={(e) => {
            setCidFilter(e.target.value);
            setPage(1);
          }}
          size="small"
          sx={{ minWidth: 140 }}
        />

        <TextField
          select
          label="Client"
          value={clientFilter}
          onChange={(e) => {
            setClientFilter(e.target.value);
            setPage(1);
          }}
          size="small"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All Clients</MenuItem>
          {clients.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          size="small"
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="in_progress">In Progress</MenuItem>
          <MenuItem value="done">Done</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
        </TextField>

        <TextField
          label="Created from"
          type="date"
          value={createdFrom}
          onChange={(e) => {
            setCreatedFrom(e.target.value);
            setPage(1);
          }}
          size="small"
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Created to"
          type="date"
          value={createdTo}
          onChange={(e) => {
            setCreatedTo(e.target.value);
            setPage(1);
          }}
          size="small"
          InputLabelProps={{ shrink: true }}
        />

        <Button
          variant="outlined"
          onClick={() => {
            setNameFilter("");
            setCidFilter("");
            setClientFilter("");
            setStatusFilter("all");
            setCreatedFrom("");
            setCreatedTo("");
            setSortBy("createdAt");
            setOrder("DESC");
            setPage(1);
          }}
        >
          Reset
        </Button>
          {error && error.length > 0 ?         <Snackbar
              open={error.length > 0}
              onClose={() => setError("")}
              autoHideDuration={3000}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
              <Alert
                  onClose={() => setError("")}
                  severity="error"
                  sx={{ width: "100%" }}
              >
                  {error}
              </Alert>
          </Snackbar> : null}
      </Stack>

      {selectedTaskIds.length > 0 && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            backgroundColor: "#eef3fb",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box sx={{ fontWeight: 600, color: "#333" }}>
            {selectedTaskIds.length} selected
          </Box>
          <TextField
            placeholder="Enter CID"
            value={bulkCidDraft}
            onChange={(e) => setBulkCidDraft(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
            disabled={isBulkSavingCid || isSavingCid || isSavingName || !!editingTaskId || !!editingNameTaskId}
          />
          <Button
            variant="contained"
            onClick={saveBulkCid}
            disabled={
              isBulkSavingCid ||
              isSavingCid ||
              isSavingName ||
              !!editingTaskId ||
              !!editingNameTaskId ||
              !bulkCidDraft
            }
          >
            Apply
          </Button>
          <Button
            variant="text"
            onClick={cancelBulkCid}
            disabled={isBulkSavingCid}
            sx={{ color: "#333" }}
          >
            Cancel
          </Button>
        </Box>
      )}

      <TableContainer>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#fafafa" }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={!isAllSelected && isSomeSelected}
                  onChange={handleSelectAll}
                  inputProps={{ "aria-label": "select all tasks" }}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#666" }} sortDirection={sortBy === "name" ? order.toLowerCase() : false}>
                <TableSortLabel
                  active={sortBy === "name"}
                  direction={sortBy === "name" ? order.toLowerCase() : "asc"}
                  onClick={() => handleRequestSort("name")}
                >
                  Task Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#666" }} sortDirection={sortBy === "createdAt" ? order.toLowerCase() : false}>
                <TableSortLabel
                  active={sortBy === "createdAt"}
                  direction={sortBy === "createdAt" ? order.toLowerCase() : "desc"}
                  onClick={() => handleRequestSort("createdAt")}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#666" }} sortDirection={sortBy === "updatedAt" ? order.toLowerCase() : false}>
                <TableSortLabel
                  active={sortBy === "updatedAt"}
                  direction={sortBy === "updatedAt" ? order.toLowerCase() : "desc"}
                  onClick={() => handleRequestSort("updatedAt")}
                >
                  Finished
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#666" }}>List Name</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#666" }}>List ID</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#666" }} sortDirection={sortBy === "client" ? order.toLowerCase() : false}>
                <TableSortLabel
                  active={sortBy === "client"}
                  direction={sortBy === "client" ? order.toLowerCase() : "asc"}
                  onClick={() => handleRequestSort("client")}
                >
                  Client
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#666" }} sortDirection={sortBy === "recordsCount" ? order.toLowerCase() : false}>
                <TableSortLabel
                  active={sortBy === "recordsCount"}
                  direction={sortBy === "recordsCount" ? order.toLowerCase() : "asc"}
                  onClick={() => handleRequestSort("recordsCount")}
                >
                  Records
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#666" }} sortDirection={sortBy === "status" ? order.toLowerCase() : false}>
                <TableSortLabel
                  active={sortBy === "status"}
                  direction={sortBy === "status" ? order.toLowerCase() : "asc"}
                  onClick={() => handleRequestSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#666" }}>CID</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#666" }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.data.map((task) => (
              <TableRow
                key={task.id}
                sx={{
                  "&:hover": { backgroundColor: "#f5f5f5" },
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedTaskIds.includes(task.id)}
                    onChange={() => handleToggleSelected(task.id)}
                    inputProps={{ "aria-label": `select task ${task.id}` }}
                  />
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {editingNameTaskId === task.id ? (
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <TextField
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        size="small"
                        variant="outlined"
                        sx={{
                          minWidth: 140,
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "#f9a825",
                              borderWidth: 2,
                            },
                          },
                        }}
                        disabled={isSavingName}
                        autoFocus
                      />
                      <IconButton
                        size="small"
                        onClick={() => saveName(task)}
                        disabled={isSavingName}
                        aria-label="save name"
                        sx={{ color: "#2e7d32" }}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={cancelEditingName}
                        disabled={isSavingName}
                        aria-label="cancel name edit"
                        sx={{ color: "#c62828" }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ) : (
                    renderEditLink(task.name, "+ Add Name", () => startEditingName(task))
                  )}
                </TableCell>
                <TableCell sx={tableCellStyle}>{formatDateTime(task.createdAt)}</TableCell>
                <TableCell sx={tableCellStyle}>
                  {task.status === 2 ? formatDateTime(task.updatedAt) : "—"}
                </TableCell>
                <TableCell sx={tableCellStyle}>{task.listName || "—"}</TableCell>
                <TableCell sx={tableCellStyle}>{task.listId || "—"}</TableCell>
                <TableCell sx={tableCellStyle}>{task.client?.name || "N/A"}</TableCell>
                <TableCell sx={tableCellStyle}>{task.recordsCount ?? 0}</TableCell>
                <TableCell sx={tableCellStyle}>
                  {getStatusChip(task.status, task.statusLabel)}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {editingTaskId === task.id ? (
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <TextField
                        value={cidDraft}
                        onChange={(e) => setCidDraft(e.target.value)}
                        size="small"
                        variant="outlined"
                        sx={{
                          minWidth: 140,
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "#f9a825",
                              borderWidth: 2,
                            },
                          },
                        }}
                        disabled={isSavingCid}
                        autoFocus
                      />
                      <IconButton
                        size="small"
                        onClick={() => saveCid(task)}
                        disabled={isSavingCid}
                        aria-label="save cid"
                        sx={{ color: "#2e7d32" }}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={cancelEditingCid}
                        disabled={isSavingCid}
                        aria-label="cancel cid edit"
                        sx={{ color: "#c62828" }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ) : (
                    renderEditLink(task.cid, "+ Add CID", () => startEditingCid(task))
                  )}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {task.status === 2 && (
                    <IconButton
                      size="small"
                      onClick={() => downloadTask(task)}
                      aria-label="download task"
                      sx={{ color: "#1976d2" }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        <Pagination
          count={tasks.meta?.totalPages || 1}
          page={page}
          onChange={(_, newPage) => setPage(newPage)}
          color="primary"
        />
      </Box>
    </React.Fragment>
  );
});

export default Tasks;
